import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import Pagination from "../view/common/pagination/Pagination";
import { ApiResponse, Currency, CurrencyResponse } from "../models/currencytypes";
import { deleteData, getData, patchData, postData, putData } from "../api/ApiServices";
import TableComponent from "./TableComponent";
import '../styles/common/sub-menu.css';
import '../styles/common/spinner.css';
import '../styles/common/icon-style.css'
import "../styles/ItemModels.css";
import '../styles/common/toplayout.css'
import '../styles/common/btn.css'
import '../styles/common/custom-alert.css';
import CustomAlert from "../view/common/alert/CustomAlert";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";

import customStyles from '../styles/currencyMaster.module.css'

const CurrencyMaster: React.FC = () => {
  const [currencyData, setCurrencyData] = useState<Currency[]>([]);
  const [currentCurrency, setCurrentCurrency] = useState<Currency>({
    code: "",
    name: "",
    status: "ACTIVE"
  });
  const [originalCurrency, setOriginalCurrency] = useState<Currency>({
    code: "",
    name: "",
    status: "ACTIVE"
  });
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [validationErrors, setValidationErrors] = useState<{
    code?: string;
    name?: string;
    status?: string;
  }>({});

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(() => { });
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(() => { });
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

    const [sortBy, setSortBy] = useState<string>("code");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions)
  const [initializeCurrencyMaster, isCurrencyMasterInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isCurrencyMasterInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeCurrencyMaster) {
      fetchCurrencies();
    }
  }, [initializeCurrencyMaster, currentPage, itemsPerPage, searchTerm, filters, sortBy, sortOrder]);

  const fetchCurrencies = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<ApiResponse<CurrencyResponse>>("/currencymaster", {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      setCurrencyData(response?.data?.data || []);
      setTotalItems(response?.data?.pagination.total);
      setTotalPages(response?.data?.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching currencies:", error);
      setError("Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => { },
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const validateForm = (): boolean => {
    const errors: {
      code?: string;
      name?: string;
      status?: string;
    } = {};

    if (!currentCurrency.code.trim()) {
      errors.code = "Currency Code is required";
    }

    if (!currentCurrency.name.trim()) {
      errors.name = "Currency Name is required";
    }

    if (editMode && !currentCurrency.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const hasChanges = (): boolean => {
    return (
      currentCurrency.code !== originalCurrency.code ||
      currentCurrency.name !== originalCurrency.name ||
      currentCurrency.status !== originalCurrency.status
    );
  };

  const handleAddCurrency = () => {
    const newCurrency = { code: "", name: "", status: "ACTIVE" };
    setCurrentCurrency(newCurrency);
    setOriginalCurrency(newCurrency);
    setValidationErrors({});
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEditCurrency = (currency: Currency) => {
    setCurrentCurrency({ ...currency });
    setOriginalCurrency({ ...currency });
    setValidationErrors({});
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return; 
    }

    if (editMode && !hasChanges()) {
      configureAlert(
        "No changes were made.",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return;
    }

    setIsSubmitting(true);
    try {
      let updatedCurrency = { ...currentCurrency };

      if (editMode) {
        // if (currentCurrency?.status === "INACTIVE") {
        //   updatedCurrency.status = "ACTIVE";
        // }

        await putData<ApiResponse<CurrencyResponse>>(
          "/currencymaster",
          currentCurrency._id as string,
          updatedCurrency
        );
        setIsModalOpen(false);

        configureAlert(
          `${currentCurrency.name} has been updated successfully.`,
          () => {
            fetchCurrencies();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        const response:any = await postData<ApiResponse<CurrencyResponse>>(
          "/currencymaster",
          updatedCurrency
        );
        setIsModalOpen(false);

        if(response.statusCode === 1999){
          configureAlert(
            `${currentCurrency.name} has been added successfully.`,
            () => {
            fetchCurrencies();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
     fetchCurrencies();
      }
    } catch (error) {
      console.error("Error saving currency:", error);
      configureAlert(
        'Duplicate Entry! ',
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCurrency = async (id: string) => {
    const currencyToDelete = currencyData.find(currency => currency._id === id);

    if (currencyToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${currencyToDelete.name}?`,
        async () => {
          closeAlert();

          try {
            await deleteData("/currencymaster", id);
            await fetchCurrencies();

            configureAlert(
              `Currency has been Inactive successfully.`,
              () => {
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting currency:", error);
            configureAlert(
              "Failed to delete currency.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handleModalClose = () => {
    if (isModalOpen && hasChanges()) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentCurrency((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrentCurrency((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "code", label: "Code" },
    { key: "name", label: "Name" },
    { key: "status", label: "Status" },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>General</span>
              <span>Currency Master</span>
            </div>
            {permission.menus.currencymaster.create ?
              <div className="items-button-container tooltip-wrapper">
                <button
                  className="item-models-add-btn"
                  onClick={handleAddCurrency}
                  disabled={isLoading}
                >
                  <Plus size={16} />
                </button>
              </div>
              : <></>}
          </div>
        </div>
      </div>

      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={currencyData}
            onEdit={handleEditCurrency}
            onDelete={handleDeleteCurrency}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }} 
          />
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />

        {isModalOpen && (
          <div className="item-models-modal-overlay">
            <div className="item-models-modal">
              <div className="item-models-modal-header">
                <h5>{editMode ? "Edit Currency" : "Add New Currency"}</h5>
                <button className="item-models-modal-close" onClick={handleModalClose}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSave} noValidate>
                <div className={customStyles["form-group"]}>
                  <input
                    type="text"
                    name="code"
                    placeholder="Currency Code"
                    value={currentCurrency.code}
                    onChange={handleInputChange}
                    className={validationErrors.code ? customStyles["input-error"] : ""}
                    required
                  />
                  {validationErrors.code && (
                    <div className={customStyles["form-error-message"]}>
                      {validationErrors.code}
                    </div>
                  )}
                </div>

                <div className={customStyles["form-group"]}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Currency Name"
                    value={currentCurrency.name}
                    onChange={handleInputChange}
                    className={validationErrors.name ? customStyles["input-error"] : ""}
                    required
                  />
                  {validationErrors.name && (
                    <div className={customStyles["form-error-message"]}>
                      {validationErrors.name}
                    </div>
                  )}
                </div>

                {editMode && (
                  <div className={customStyles["select-container"]}>
                    <select
                      name="status"
                      value={currentCurrency.status}
                      onChange={handleSelectChange}
                      className={validationErrors.status ? customStyles["select-error"] : ""}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                    {validationErrors.status && (
                      <div className={customStyles["form-error-message"]}>
                        {validationErrors.status}
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={customStyles["submit-button"]}
                >
                  {isSubmitting ? "Saving..." : editMode ? "Update" : "Add"}
                </button>
              </form>
            </div>
          </div>
        )}

        {showAlert && (
          <CustomAlert
            message={alertMessage}
            onConfirm={alertConfirmAction}
            onCancel={alertCancelAction}
            confirmText={confirmButtonText}
            cancelText={cancelButtonText}
            showConfirmButton={showConfirmButton}
            showCancelButton={showCancelButton}
            autoCloseDelay={autoCloseDelay}
          />
        )}
      </div>
    </div>
  );
};

export default CurrencyMaster;